import { FilterDropdownOption } from '@components/modules/ProductGrid/Filter/FilterDropdown'
import {
  UrlFilter,
  parseQueryAttributeFilters,
  serializeQueryAttributeFilters,
} from '@components/modules/ProductGrid/Filter/filtering'
import { CustomProductGridTile } from '@features/products/api/get-custom-product-grid-tiles'
import { isAvailableForPurchase } from '@lib/is-available-for-purchase'
import { mapEdgesToItems } from '@utils/map'
import { useQueryState } from 'next-usequerystate'
import { useCallback, useMemo } from 'react'

export const useFilterProducts: (
  initialProducts: CustomProductGridTile[],
  soldOnline: boolean,
) => {
  staticProducts: CustomProductGridTile[]
  onChangeQueryFilter: (
    slug: string,
    options: FilterDropdownOption[],
  ) => Promise<URLSearchParams>
  removeQueryFilter: (slug: string, value: string) => Promise<URLSearchParams>
  queryFilters: UrlFilter[]
} = (initialProducts, soldOnline) => {
  const [queryFilters, setQueryFilters] = useQueryState('filters', {
    parse: parseQueryAttributeFilters,
    serialize: serializeQueryAttributeFilters,
    defaultValue: [],
    shallow: false,
  })

  const staticProducts = useMemo(() => {
    if (queryFilters.length > 0 || soldOnline) {
      return initialProducts.filter((x) => {
        const variants = mapEdgesToItems(x.variants)

        if (
          soldOnline &&
          (!variants ||
            !variants.some((v) => isAvailableForPurchase(v.availability)))
        ) {
          return false
        }

        if (queryFilters.length === 0) return true

        return queryFilters.every((f) => {
          if (
            f.slug === 'collections' ||
            f.slug === 'designers' ||
            f.slug === 'types'
          ) {
            return f.values.some((val) =>
              x.tags.some((tag) => tag.toLowerCase() === val.toLowerCase()),
            )
          }

          return true
        })
      })
    }
    return initialProducts
  }, [soldOnline, queryFilters])

  const onChangeQueryFilter = useCallback(
    (slug: string, options: FilterDropdownOption[]) => {
      //check if filter is already applied for that slug
      const existingFilter = queryFilters.find((x) => x.slug === slug)
      if (!existingFilter) {
        return setQueryFilters([
          ...queryFilters,
          { slug: slug, values: options.map((x) => x.value) },
        ])
      }

      existingFilter.values = options.map((x) => x.value)

      // if a filter has no values, remove it from the query
      if (existingFilter.values.length === 0) {
        return setQueryFilters(
          queryFilters.filter((x) => x.slug !== existingFilter.slug),
        )
      }

      return setQueryFilters([...queryFilters])
    },
    [queryFilters],
  )

  const removeQueryFilter = useCallback(
    (slug: string, value: string) => {
      const newFilters = queryFilters.reduce(
        (result: UrlFilter[], filter: UrlFilter) => {
          if (filter.slug !== slug) {
            return [...result, filter]
          }
          const newFilterValues = filter.values.filter((val) => val !== value)
          if (newFilterValues?.length) {
            return [...result, { ...filter, values: newFilterValues }]
          }
          return result
        },
        [],
      )

      return setQueryFilters(newFilters.length ? newFilters : null)
    },
    [queryFilters],
  )

  return {
    staticProducts,
    onChangeQueryFilter,
    removeQueryFilter,
    queryFilters,
  }
}
